import React, { useState, useMemo } from 'react';
import { TemperatureLog } from '../../../types/temperatureLog';
import { TemperatureEquipment } from '../../../types/temperatureEquipment';
import { matchesSearchQuery } from '../../../utils/searchUtils';

interface TemperatureLogTableProps {
  logs: TemperatureLog[];
  equipment: TemperatureEquipment[];
  type: 'freezer' | 'oven' | 'fridge';
  onAdd: (log: Partial<TemperatureLog>) => void;
  onUpdate: (id: string, log: Partial<TemperatureLog>) => void;
  onDelete: (id: string) => void;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (timeStr: string) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

const getMonthKey = (dateStr: string) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const TemperatureLogTable: React.FC<TemperatureLogTableProps> = ({
  logs,
  equipment,
  type,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});

  const today = new Date().toISOString().split('T')[0];
  const currentTime = new Date().toTimeString().slice(0, 5);

  const blankLog = {
    equipmentId: '',
    date: today,
    timeChecked: currentTime,
    actualTemp: '',
    correctiveAction: '',
    recordedBy: '',
  };

  const [newLog, setNewLog] = useState(blankLog);
  const [editLog, setEditLog] = useState(blankLog);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter logs based on search
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return logs;
    return logs.filter(log => matchesSearchQuery(log, searchTerm, [log.equipmentName || '']));
  }, [logs, searchTerm]);

  // Group logs by month
  const groupedLogs = useMemo(() => {
    const groups: { key: string; logs: TemperatureLog[] }[] = [];
    const map = new Map<string, TemperatureLog[]>();

    for (const log of filteredLogs) {
      const key = getMonthKey(log.date);
      if (!map.has(key)) {
        map.set(key, []);
        groups.push({ key, logs: map.get(key)! });
      }
      map.get(key)!.push(log);
    }

    // Initialize expanded state for new months
    for (const group of groups) {
      if (!(group.key in expandedMonths)) {
        expandedMonths[group.key] = groups.indexOf(group) === 0; // Expand most recent
      }
    }

    return groups;
  }, [filteredLogs]);

  const toggleMonth = (key: string) => {
    setExpandedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleStartAdd = () => {
    setIsAddingNew(true);
    setEditingId(null);
    setNewLog(blankLog);
  };

  const handleSaveNew = () => {
    if (!newLog.equipmentId || !newLog.date) return;
    onAdd({
      equipmentId: newLog.equipmentId,
      date: newLog.date,
      timeChecked: newLog.timeChecked || null,
      actualTemp: newLog.actualTemp !== '' ? Number(newLog.actualTemp) : null,
      correctiveAction: newLog.correctiveAction,
      recordedBy: newLog.recordedBy,
    } as Partial<TemperatureLog>);
    setIsAddingNew(false);
    setNewLog(blankLog);
  };

  const handleStartEdit = (log: TemperatureLog) => {
    setEditingId(log.id);
    setIsAddingNew(false);
    setEditLog({
      equipmentId: log.equipmentId,
      date: log.date ? log.date.split('T')[0] : '',
      timeChecked: log.timeChecked || '',
      actualTemp: log.actualTemp != null ? String(log.actualTemp) : '',
      correctiveAction: log.correctiveAction || '',
      recordedBy: log.recordedBy || '',
    });
  };

  const handleSaveEdit = () => {
    if (!editingId || !editLog.equipmentId || !editLog.date) return;
    onUpdate(editingId, {
      equipmentId: editLog.equipmentId,
      date: editLog.date,
      timeChecked: editLog.timeChecked || null,
      actualTemp: editLog.actualTemp !== '' ? Number(editLog.actualTemp) : null,
      correctiveAction: editLog.correctiveAction,
      recordedBy: editLog.recordedBy,
    } as Partial<TemperatureLog>);
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this temperature log entry?')) {
      onDelete(id);
    }
  };

  const getAcceptableRange = (equipmentId: string) => {
    const eq = equipment.find((e) => e.id === equipmentId);
    if (!eq) return '—';
    if (eq.tempRangeMin != null && eq.tempRangeMax != null) {
      return `${eq.tempRangeMin}°F – ${eq.tempRangeMax}°F`;
    }
    if (eq.tempRangeMin != null) return `≥ ${eq.tempRangeMin}°F`;
    if (eq.tempRangeMax != null) return `≤ ${eq.tempRangeMax}°F`;
    return '—';
  };

  const isOutOfRange = (log: TemperatureLog) => {
    if (log.actualTemp == null) return false;
    const eq = equipment.find((e) => e.id === log.equipmentId);
    if (!eq) return false;
    if (eq.tempRangeMin != null && log.actualTemp < eq.tempRangeMin) return true;
    if (eq.tempRangeMax != null && log.actualTemp > eq.tempRangeMax) return true;
    return false;
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'background-color 0.2s',
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '13px',
    boxSizing: 'border-box',
  };

  const thStyle: React.CSSProperties = {
    padding: '10px 8px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '13px',
    fontWeight: '600',
  };

  const tdStyle: React.CSSProperties = {
    padding: '8px',
    borderBottom: '1px solid #eee',
    fontSize: '13px',
  };

  const renderFormRow = (
    values: typeof newLog,
    onChange: (field: string, value: string) => void,
    onSave: () => void,
    onCancel: () => void
  ) => (
    <tr style={{ backgroundColor: '#f9fbe7' }}>
      <td style={tdStyle}>
        <input
          style={{ ...inputStyle, width: '130px' }}
          type="date"
          value={values.date}
          onChange={(e) => onChange('date', e.target.value)}
        />
      </td>
      <td style={tdStyle}>
        <select
          style={{ ...inputStyle, width: '160px' }}
          value={values.equipmentId}
          onChange={(e) => onChange('equipmentId', e.target.value)}
        >
          <option value="">Select equipment...</option>
          {equipment.map((eq) => (
            <option key={eq.id} value={eq.id}>
              {eq.equipmentName}
            </option>
          ))}
        </select>
      </td>
      <td style={tdStyle}>
        <input
          style={{ ...inputStyle, width: '110px' }}
          type="time"
          value={values.timeChecked}
          onChange={(e) => onChange('timeChecked', e.target.value)}
        />
      </td>
      <td style={tdStyle}>
        <input
          style={{ ...inputStyle, width: '80px' }}
          type="number"
          placeholder="°F"
          value={values.actualTemp}
          onChange={(e) => onChange('actualTemp', e.target.value)}
        />
      </td>
      <td style={tdStyle}>
        <span style={{ color: '#999', fontSize: '12px' }}>
          {values.equipmentId ? getAcceptableRange(values.equipmentId) : '—'}
        </span>
      </td>
      <td style={tdStyle}>
        <input
          style={{ ...inputStyle, width: '140px' }}
          placeholder="Corrective action"
          value={values.correctiveAction}
          onChange={(e) => onChange('correctiveAction', e.target.value)}
        />
      </td>
      <td style={tdStyle}>
        <input
          style={{ ...inputStyle, width: '140px' }}
          placeholder="Recorded by"
          value={values.recordedBy}
          onChange={(e) => onChange('recordedBy', e.target.value)}
        />
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        <button
          style={{ ...buttonStyle, backgroundColor: '#4CAF50', color: 'white', marginRight: '6px' }}
          onClick={onSave}
        >
          Save
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: '#757575', color: 'white' }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </td>
    </tr>
  );

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        {!isAddingNew && (
          <button
            style={{ ...buttonStyle, backgroundColor: '#4CAF50', color: 'white' }}
            onClick={handleStartAdd}
          >
            + Add Log Entry
          </button>
        )}
      </div>

      {/* Search */}
      {!isAddingNew && !editingId && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search temperature logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: '6px' }}>
        <thead>
          <tr>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Equipment</th>
            <th style={thStyle}>Time Checked</th>
            <th style={thStyle}>Actual Temp</th>
            <th style={thStyle}>Acceptable Range</th>
            <th style={thStyle}>Corrective Action</th>
            <th style={thStyle}>Recorded By</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isAddingNew &&
            renderFormRow(
              newLog,
              (field, value) => setNewLog((prev) => ({ ...prev, [field]: value })),
              handleSaveNew,
              () => setIsAddingNew(false)
            )}

          {groupedLogs.length === 0 && !isAddingNew && (
            <tr>
              <td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
                No log entries yet. Click "+ Add Log Entry" to get started.
              </td>
            </tr>
          )}
          {logs.length > 0 && filteredLogs.length === 0 && !isAddingNew && (
            <tr>
              <td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                No logs match your search for "{searchTerm}".
              </td>
            </tr>
          )}

          {groupedLogs.map((group) => (
            <React.Fragment key={group.key}>
              <tr
                onClick={() => toggleMonth(group.key)}
                style={{ cursor: 'pointer', backgroundColor: '#f0f4f8' }}
              >
                <td
                  colSpan={8}
                  style={{
                    padding: '10px 8px',
                    fontWeight: '600',
                    fontSize: '14px',
                    borderBottom: '1px solid #ddd',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                      transform: expandedMonths[group.key] ? 'rotate(90deg)' : 'rotate(0deg)',
                      marginRight: '8px',
                    }}
                  >
                    ▸
                  </span>
                  {group.key} ({group.logs.length} {group.logs.length === 1 ? 'entry' : 'entries'})
                </td>
              </tr>

              {expandedMonths[group.key] &&
                group.logs.map((log) =>
                  editingId === log.id ? (
                    <React.Fragment key={log.id}>
                      {renderFormRow(
                        editLog,
                        (field, value) => setEditLog((prev) => ({ ...prev, [field]: value })),
                        handleSaveEdit,
                        () => setEditingId(null)
                      )}
                    </React.Fragment>
                  ) : (
                    <tr key={log.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={tdStyle}>{formatDate(log.date)}</td>
                      <td style={tdStyle}>{log.equipmentName || '—'}</td>
                      <td style={tdStyle}>{log.timeChecked ? formatTime(log.timeChecked) : '—'}</td>
                      <td
                        style={{
                          ...tdStyle,
                          color: isOutOfRange(log) ? '#f44336' : 'inherit',
                          fontWeight: isOutOfRange(log) ? '600' : 'normal',
                        }}
                      >
                        {log.actualTemp != null ? `${log.actualTemp}°F` : '—'}
                      </td>
                      <td style={{ ...tdStyle, color: '#666', fontSize: '12px' }}>
                        {log.acceptableRangeMin != null && log.acceptableRangeMax != null
                          ? `${log.acceptableRangeMin}°F – ${log.acceptableRangeMax}°F`
                          : getAcceptableRange(log.equipmentId)}
                      </td>
                      <td style={tdStyle}>{log.correctiveAction || '—'}</td>
                      <td style={tdStyle}>{log.recordedBy || '—'}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <button
                          style={{ ...buttonStyle, backgroundColor: '#2196F3', color: 'white', marginRight: '6px' }}
                          onClick={() => handleStartEdit(log)}
                        >
                          Edit
                        </button>
                        <button
                          style={{ ...buttonStyle, backgroundColor: '#f44336', color: 'white' }}
                          onClick={() => handleDelete(log.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
